import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import MapView from '@arcgis/core/views/MapView';
import {NbThemeService} from '@nebular/theme';
import Layer from '@arcgis/core/layers/Layer';
import Extent from '@arcgis/core/geometry/Extent';
import {CoordinateConverterService} from '@core/utils/engin/geospatial-viewer/helper/coordinate-converter.service';
import Map from '@arcgis/core/Map';
import {SpatialCardVm} from '@theme/components/card-components/spatial-card/spatial-card.vm';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {BehaviorSubject, Observable} from 'rxjs';
import {GenerateLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import Basemap from '@arcgis/core/Basemap';
import {takeUntil, debounceTime} from 'rxjs/operators';
import {GeospatialCenterCoordinate} from '../../../../../pages/geospatial-viewer/model/viewer-config';
import WebMap from '@arcgis/core/WebMap';
import Home from '@arcgis/core/widgets/Home';
import {
    SpatialCardConfigBase,
    SpatialCardHeader,
} from '@theme/components/card-components/spatial-card/model/model-spatial-card';
import {BucketDto} from '../../../../../pages/geospatial-viewer/layers/metric-layer';

@Component({
    selector: 'ngx-spatial-card-base',
    template: '',
    styleUrls: ['./spatial-card-base.component.scss', '../../../../styles/esri-overrides.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class SpatialCardBaseComponent<T extends SpatialCardConfigBase>
    extends Unsubscribable
    implements OnInit
{
    @ViewChild('mapViewNode', {static: true}) public mapViewEl: ElementRef;
    // Map card title and subtitle
    @Input() title: string = 'Spatial Card';
    @Input() subtitle: string;
    @Input() headerData: Observable<SpatialCardHeader[]> = new BehaviorSubject(null);
    // Events: change map location, select feature
    @Output() onMapLocationChange: EventEmitter<T> = new EventEmitter();
    @Output() onMapFeatureSelected: EventEmitter<any> = new EventEmitter();
    // Internal tracking for stages of map component initialization and initial render
    public mapInitializedFlag = new BehaviorSubject(false);
    public mapRenderFlag = new BehaviorSubject(false);
    // Map elements
    public layerIdAlias: string = 'layer';
    public mapView: MapView;
    public map: Map;
    // Data for map component: legend
    public legendBuckets: BehaviorSubject<BucketDto[]> = new BehaviorSubject<BucketDto[]>(null);
    readonly legendBuckets$: Observable<BucketDto[]> = this.legendBuckets.asObservable();

    MAX_ZOOM_LEVEL = 18;
    MIN_ZOOM_LEVEL = 4;

    protected constructor(
        public nbThemeService: NbThemeService,
        public vm: SpatialCardVm,
        public generateLayerService: GenerateLayerService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscribeToThemeChanges();
        this.subscribeToMapChanges();
    }

    /** Handles changes for css theme */
    public subscribeToThemeChanges(): void {
        this.nbThemeService
            .onThemeChange()
            .pipe(takeUntil(this.unsubscribe$), debounceTime(500))
            .subscribe((themeChange) => {
                this.onChangeForTheme(themeChange);
            });
    }

    /** Initialize and configure the esri map view object. */
    public initializeMapSequence(config: T): void {
        try {
            // standard features
            this.initializeMapStandardFeatures(config);
            // set flag that we're done
            this.mapInitializedFlag.next(true);
        } catch (error) {}
    }

    /** Rendering of map content and location */
    protected abstract subscribeToMapChanges(): void;

    /** Support for select map feature. This may emit `onMapFeatureSelected`.  */
    protected abstract onSelectMapFeature(firstGraphic: any, allHits: any[]): void;

    /** Implementation to propagate map viewport changes. This should call `onMapLocationChange`. */
    protected abstract onMapViewportChange(newCoordinate: GeospatialCenterCoordinate): void;

    /** Initializes standard map features with rendered, drag, and zoom event handlers */
    protected initializeMapStandardFeatures(config: T): void {
        this.initializeMapBase();
        this.initializeMapView(config);
        this.initializeMapHasRendered();
        this.initializeMapDragEvent();
        this.initializeMapZoomEvent();
        this.initializeMapButtons();
        this.initializeMapClickEvents();
    }

    /** Create basemap */
    private initializeMapBase(): void {
        this.map = new WebMap({
            basemap: this.vm.getBasemapName(this.nbThemeService.currentTheme),
            layers: [],
        });
    }

    /** Create view, add mapview and basic settings */
    private initializeMapView(config: T): void {
        this.mapView = new MapView({
            container: this.mapViewEl.nativeElement,
            map: this.map,
            zoom: config.centerCoordinate.zoomLevel,
            center: [config.centerCoordinate.longitude, config.centerCoordinate.latitude],
            constraints: {
                snapToZoom: false,
                maxZoom: this.MAX_ZOOM_LEVEL, // lock max zoom
                minZoom: this.MIN_ZOOM_LEVEL, // lock min zoom
            },
        });
    }

    /** Flag when map has been initially rendered */
    private initializeMapHasRendered(): void {
        this.mapView.when().then(() => {
            this.updateMapViewport(this.mapView.extent, this.mapView.zoom);
            this.mapRenderFlag.next(true);
        });
    }

    /** Trigger viewport change events for drag */
    private initializeMapDragEvent(): void {
        this.mapView.on('drag', (drag) => {
            if (drag.action === 'end') {
                this.updateMapViewport(this.mapView.extent, this.mapView.zoom);
            }
        });
    }

    /** Trigger viewport change events for zoom */
    private initializeMapZoomEvent(): void {
        this.mapView.watch('zoom', (zoomLevel) => {
            // Zoom level is set to -1 momentarily after basemap change
            if (this.mapView.extent && zoomLevel > 0) {
                this.updateMapViewport(this.mapView.extent, zoomLevel);
            }
        });
    }

    /** Add map buttons: +/- zoom, home */
    private initializeMapButtons(): void {
        const homeBtn = new Home({view: this.mapView});
        this.mapView.ui.add(homeBtn, 'bottom-left');
        this.mapView.ui.move('zoom', 'bottom-left');
    }

    /** Monitor map click events, broadcast event when we hit a feature. */
    private initializeMapClickEvents(): void {
        this.mapView.on('click', (event) => {
            this.mapView.hitTest(event).then((response: any) => {
                // response.results will always return basemap; any feature layer will display first (I think?)
                const allHits = response.results.filter((r) => r.graphic.layer.id.includes(this.layerIdAlias));
                const firstGraphic = allHits[0]?.graphic;
                if (!!firstGraphic) {
                    this.onSelectMapFeature(firstGraphic, allHits);
                }
            });
        });
    }

    /** Swap basemap when theme changes. */
    private async onChangeForTheme(themeChange): Promise<void> {
        if (themeChange) {
            const currentTheme: string = themeChange.name;
            const previousTheme: string = themeChange.previous;

            if (currentTheme && previousTheme) {
                const currentBasemap = this.vm.getBasemapName(currentTheme);
                const previousBasemap = this.vm.getBasemapName(previousTheme);
                if (currentBasemap != previousBasemap) {
                    this.map.basemap = Basemap.fromId(currentBasemap);
                }
            }
        }
    }

    /**
     * Update the map viewport tracking variables to stay up-to-date with any map pan/zoom event. This will call
     * `onMapViewportChange` which can capture custom reactions.
     * */
    public updateMapViewport(extent: Extent, zoomLevel: number): void {
        // 'extent' extends 'geometry' and can use the same conversion
        const latLongExtent: Extent = CoordinateConverterService.convertEsriGeometry(extent, 4326) as Extent;
        const newCoordinate: GeospatialCenterCoordinate = {
            latitude: latLongExtent.center.latitude,
            longitude: latLongExtent.center.longitude,
            xmin: latLongExtent.xmin,
            xmax: latLongExtent.xmax,
            ymin: latLongExtent.ymin,
            ymax: latLongExtent.ymax,
            zoomLevel: zoomLevel,
        };
        this.onMapViewportChange(newCoordinate);
    }

    /** Remove existing map layers based on pattern matching against the layer ID */
    public removeMapLayers(): void {
        const targetLayers: Layer[] = [];
        this.map.allLayers.forEach((l: Layer) => {
            if (l.id.includes(this.layerIdAlias)) {
                targetLayers.push(l);
            }
        });
        this.map.removeMany(targetLayers);
    }
}
